import {
  strapiCreateDynamicEntity,
  strapiUpdateDynamicEntity,
  type DynamicEntityRow,
} from './strapiDynamicEntity';
import { STRAPI_URL, strapiAuthHeaders } from './strapi';

export const JENIS_PENGUKURAN_PERLENGKAPAN_API = 'jenis-pengukuran-perlengkapans';

export type JenisPengukuranPerlengkapanFields = {
  perlengkapan_pengambilan_contoh: DynamicEntityRow[];
  perlengkapan_pengukuran_lapangan: DynamicEntityRow[];
  perlengkapan_pendukung: DynamicEntityRow[];
  perlengkapan_k3: DynamicEntityRow[];
};

export type JenisPengukuranPerlengkapanMapping = JenisPengukuranPerlengkapanFields & {
  documentId: string;
  jenisPengukuranDocumentId: string;
};

/** Checklist sampling plan masih memakai field `peralatan_*`; ini shape preload dari master perlengkapan. */
export type SamplingPlanPeralatanPreload = {
  peralatan_pengambilan_contoh: DynamicEntityRow[];
  peralatan_pengukuran_lapangan: DynamicEntityRow[];
  peralatan_pendukung: DynamicEntityRow[];
  peralatan_k3: DynamicEntityRow[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function flattenRelationEntry(raw: unknown): DynamicEntityRow | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const attrs = asRecord(rec.attributes);
  const documentId = String(rec.documentId ?? attrs?.documentId ?? '').trim();
  if (!documentId) return null;
  const merged = attrs ? { ...attrs, documentId } : { ...rec, documentId };
  return merged as DynamicEntityRow;
}

/** Normalize Strapi relation field (single or many) into DynamicEntityRow[]. */
export function relationFieldToRows(field: unknown): DynamicEntityRow[] {
  if (!field) return [];
  if (Array.isArray(field)) {
    return field.map(flattenRelationEntry).filter((r): r is DynamicEntityRow => r != null);
  }
  const rec = asRecord(field);
  if (rec && Array.isArray(rec.data)) {
    return relationFieldToRows(rec.data);
  }
  const one = flattenRelationEntry(field);
  return one ? [one] : [];
}

function jenisPengukuranDocIdFromRow(row: DynamicEntityRow): string {
  const jp = row.jenis_pengukuran;
  const fromRel = relationFieldToRows(jp)[0]?.documentId;
  return String(fromRel ?? '').trim();
}

function rowToMapping(row: DynamicEntityRow): JenisPengukuranPerlengkapanMapping | null {
  const jenisPengukuranDocumentId = jenisPengukuranDocIdFromRow(row);
  if (!jenisPengukuranDocumentId) return null;
  return {
    documentId: row.documentId,
    jenisPengukuranDocumentId,
    perlengkapan_pengambilan_contoh: relationFieldToRows(row.perlengkapan_pengambilan_contoh),
    perlengkapan_pengukuran_lapangan: relationFieldToRows(row.perlengkapan_pengukuran_lapangan),
    perlengkapan_pendukung: relationFieldToRows(row.perlengkapan_pendukung),
    perlengkapan_k3: relationFieldToRows(row.perlengkapan_k3),
  };
}

function connectMany(rows: DynamicEntityRow[]): { connect: string[] } {
  const ids = rows.map((r) => String(r.documentId ?? '').trim()).filter(Boolean);
  return { connect: ids };
}

export function buildJenisPengukuranPerlengkapanWritePayload(
  jenisPengukuranDocumentId: string,
  fields: JenisPengukuranPerlengkapanFields,
): Record<string, unknown> {
  return {
    jenis_pengukuran: { connect: [jenisPengukuranDocumentId] },
    perlengkapan_pengambilan_contoh: connectMany(fields.perlengkapan_pengambilan_contoh),
    perlengkapan_pengukuran_lapangan: connectMany(fields.perlengkapan_pengukuran_lapangan),
    perlengkapan_pendukung: connectMany(fields.perlengkapan_pendukung),
    perlengkapan_k3: connectMany(fields.perlengkapan_k3),
  };
}

/** Load master mapping rows for the given jenis pengukuran documentIds. */
export async function strapiListJenisPengukuranPerlengkapanByJenisIds(
  jwt: string,
  jenisPengukuranDocumentIds: string[],
): Promise<Map<string, JenisPengukuranPerlengkapanMapping>> {
  const ids = [...new Set(jenisPengukuranDocumentIds.map((id) => id.trim()).filter(Boolean))];
  const out = new Map<string, JenisPengukuranPerlengkapanMapping>();
  if (!ids.length) return out;

  await Promise.all(
    ids.map(async (jenisId) => {
      const params = new URLSearchParams({
        'pagination[pageSize]': '5',
        'pagination[page]': '1',
        sort: 'updatedAt:desc',
        populate: '*',
      });
      params.append('filters[jenis_pengukuran][documentId][$eq]', jenisId);
      const res = await fetch(
        `${STRAPI_URL}/api/${encodeURIComponent(JENIS_PENGUKURAN_PERLENGKAPAN_API)}?${params}`,
        { headers: strapiAuthHeaders(jwt) },
      );
      const body = (await res.json().catch(() => ({}))) as { data?: unknown[]; error?: { message?: string } };
      if (!res.ok) {
        throw new Error(body.error?.message ?? `Gagal memuat mapping perlengkapan (${res.status})`);
      }
      const rows = Array.isArray(body.data) ? body.data : [];
      const mapping = rows
        .map((raw) => {
          const rec = asRecord(raw);
          if (!rec) return null;
          const attrs = asRecord(rec.attributes);
          const documentId = String(rec.documentId ?? attrs?.documentId ?? '').trim();
          const flat = attrs ? { ...attrs, documentId } : { ...rec, documentId };
          return rowToMapping(flat as DynamicEntityRow);
        })
        .find(Boolean);
      if (mapping) out.set(jenisId, mapping);
    }),
  );

  return out;
}

export async function strapiUpsertJenisPengukuranPerlengkapan(
  jwt: string,
  jenisPengukuranDocumentId: string,
  fields: JenisPengukuranPerlengkapanFields,
  existingDocumentId?: string | null,
): Promise<JenisPengukuranPerlengkapanMapping> {
  const payload = buildJenisPengukuranPerlengkapanWritePayload(jenisPengukuranDocumentId, fields);
  const existingId = String(existingDocumentId ?? '').trim();

  const row = existingId
    ? await strapiUpdateDynamicEntity(jwt, JENIS_PENGUKURAN_PERLENGKAPAN_API, existingId, payload, '*')
    : await strapiCreateDynamicEntity(jwt, JENIS_PENGUKURAN_PERLENGKAPAN_API, payload, '*');

  const mapping = rowToMapping(row);
  if (!mapping) throw new Error('Respons mapping jenis pengukuran × perlengkapan tidak valid.');
  return mapping;
}

/** Map master perlengkapan → field peralatan di checklist sampling plan. */
export function samplingPlanPeralatanPreloadFromMapping(
  mapping: JenisPengukuranPerlengkapanMapping | undefined,
): Partial<SamplingPlanPeralatanPreload> {
  if (!mapping) return {};
  return {
    peralatan_pengambilan_contoh: mapping.perlengkapan_pengambilan_contoh,
    peralatan_pengukuran_lapangan: mapping.perlengkapan_pengukuran_lapangan,
    peralatan_pendukung: mapping.perlengkapan_pendukung,
    peralatan_k3: mapping.perlengkapan_k3,
  };
}

export function countPerlengkapanFields(fields: JenisPengukuranPerlengkapanFields): number {
  return (
    fields.perlengkapan_pengambilan_contoh.length +
    fields.perlengkapan_pengukuran_lapangan.length +
    fields.perlengkapan_pendukung.length +
    fields.perlengkapan_k3.length
  );
}
